/*
 * A status a person publishes about themselves: an emoji out of a closed palette, a short line, and
 * the moment it clears itself.
 *
 * Everything a status *is* is said once here, in the order below: whether a person publishes one at
 * all and how it reads, what it may carry, when it ends, the presets that fill the whole of it in one
 * press, and the single write a save makes. The two pictures of it are `PersonStatus.tsx`, the form
 * is `StatusDialog.tsx`, the door is `core/shell/AccountMenu.tsx`, and none of the three asks the
 * four wire fields a question of its own.
 *
 * The palette itself is not this file's: it is one generated constant from the server
 * (`internal/core/wire/status.go` → `core/wire/core.gen.ts`), because the set the picker offers and
 * the set the write path accepts are one set, and a second copy here could only ever disagree with
 * the one that refuses. The *names* of those emoji are this file's, because they are copy.
 *
 * A status is the opposite of presence (`security.md` §9.6): presence is observed by the transport
 * and stored nowhere, a status is written by the person and lives on their account. The one place
 * the two meet is the away claim, which rides the same dot — that merge belongs to `presence.ts`.
 */

// Type only, and deliberately so: this file names the shape of the write and never makes one.
import type { ProfilePatch } from "../auth/index.ts"
import {
	endOfDay,
	endOfWeek,
	formatDayTime,
	formatTime,
	sameDay,
	t,
	type PlainKey,
} from "../i18n/index.ts"
import type { PublicUser, SelfUser } from "../store/model.ts"
import { StatusEmojiPalette } from "../wire/core.gen.ts"

// ---- what a status is, and how it reads ----

/** What a person publishes, as every surface that draws one receives it. */
export interface Status {
	emoji: string
	text: string
	/** Epoch-ms when it clears itself; 0 is "never", which is how the wire's absent field arrives. */
	until: number
}

/**
 * The same thing plus the away claim, which is what the owner's own form holds: a form needs a total
 * shape — an end that is not set is the number 0 in a `<select>`, not a missing property.
 */
export interface StatusFields extends Status {
	away: boolean
}

/** The one place in the client that reads the four `omitempty` fields off an account. */
export function statusFields(user: PublicUser | SelfUser): StatusFields {
	return {
		emoji: user.statusEmoji ?? "",
		text: user.statusText ?? "",
		until: user.statusUntil ?? 0,
		away: user.statusAway === true,
	}
}

/**
 * The status of a person, or `null` where there is nothing to draw.
 *
 * Two of the four fields are deliberately not a status of their own. An end says nothing by itself —
 * the client never writes one, because clearing is the emoji, the line and the moment together — and
 * the away claim is a **dot** and not a line (`presence.ts`): somebody who has only ticked *Show me
 * as away* has published a state, not a sentence, and a line drawn for them would read empty.
 */
export function statusOf(user: PublicUser | SelfUser | null | undefined): Status | null {
	if (user === null || user === undefined) return null
	const { emoji, text, until } = statusFields(user)
	if (emoji === "" && text === "") return null
	return { emoji, text, until }
}

/**
 * What a status is called where a surface has room for a name and not for the thing itself — a menu
 * row, the accessible name of a glyph in a list: the words the person wrote, else the name of the
 * emoji they picked.
 *
 * Nothing has no name, and the one row that asks for one — the menu's — goes on offering to set a
 * status rather than announcing one nobody wrote.
 */
export function statusName(status: Status | null | undefined): string {
	if (status === null || status === undefined) return ""
	if (status.text !== "") return status.text
	return emojiName(status.emoji)
}

/**
 * "until 13:00" for an end today, "until 20 September 17:40" for a later one, and nothing at all for
 * a status that does not end. The moment is spelled the way every moment in this interface is, in
 * the account's own zone and on its own clock (`core/i18n/format.ts`).
 *
 * An end that has already passed is still spelled out. The server's minutely job decides when a
 * status goes (`protocol.md` §2.6), so for up to a minute a reader holds one whose moment is behind
 * them; blanking the line on the reader's own clock would make two browsers disagree about what one
 * person published.
 */
export function formatUntil(until: number, now: number): string {
	if (until <= 0) return ""
	return sameDay(until, now)
		? t("people.status.until", { time: formatTime(until) })
		: t("people.status.untilDate", { date: formatDayTime(until, now) })
}

// ---- what a status may carry ----

/**
 * The emoji a status may carry, in the order the picker offers them. The server ships them as one
 * space-separated constant and this is the only place it is taken apart.
 */
export const statusEmojis: readonly string[] = StatusEmojiPalette.split(/\s+/).filter(
	(emoji) => emoji !== "",
)

/**
 * How long a line may be. It is the server's own bound for a profile text
 * (`auth/identity.go`, `maxProfileGraphemes`), repeated as the `maxLength` of the box so that the
 * limit is felt while typing rather than after the save. The server counts grapheme clusters and a
 * `maxLength` counts UTF-16 units, of which there are never fewer — so the box can only ever be
 * stricter than the law it stands for, never looser.
 */
export const MAX_STATUS_TEXT = 64

/**
 * What each emoji is called, for the reader who cannot see it.
 *
 * It is a lookup and never a second copy of the palette: the picker walks `statusEmojis` and asks
 * here for a name, so a member the copy has not caught up with is still offered — named by its own
 * character rather than left out of the choice. `status.test.ts` fails while one is unnamed, which
 * is what keeps the copy in step with the server's list.
 */
const EMOJI_NAMES: Record<string, PlainKey> = {
	"🗓️": "people.status.emoji.calendar",
	"🍽️": "people.status.emoji.meal",
	"🤒": "people.status.emoji.sick",
	"🌴": "people.status.emoji.palm",
	"🚌": "people.status.emoji.bus",
	"💤": "people.status.emoji.sleep",
	"🎧": "people.status.emoji.headphones",
	"🏠": "people.status.emoji.house",
	"✈️": "people.status.emoji.plane",
	"🚗": "people.status.emoji.car",
	"👶": "people.status.emoji.baby",
	"🎓": "people.status.emoji.graduation",
	"☕": "people.status.emoji.coffee",
	"🏃": "people.status.emoji.running",
	"📞": "people.status.emoji.phone",
	"💻": "people.status.emoji.laptop",
	"🔕": "people.status.emoji.bellOff",
	"🎉": "people.status.emoji.party",
	"🤝": "people.status.emoji.handshake",
	"✍️": "people.status.emoji.writing",
	"🧘": "people.status.emoji.meditation",
	"🚧": "people.status.emoji.roadworks",
	"🌙": "people.status.emoji.moon",
	"⏳": "people.status.emoji.hourglass",
}

/** What one emoji of the palette is called; an unnamed member answers with itself. */
export function emojiName(emoji: string): string {
	const key = EMOJI_NAMES[emoji]
	return key === undefined ? emoji : t(key)
}

// ---- when a status ends ----

/**
 * When a status ends. `keep` is not one of the offered ends but the answer "the one it already
 * has", which is what a dialog opened to fix a typo must not throw away.
 */
export type ClearAfter = "30m" | "1h" | "4h" | "today" | "week" | "never"

/** The ends a status may be given, in the order they are offered. */
export const CLEAR_AFTER = ["30m", "1h", "4h", "today", "week", "never"] as const

const CLEAR_AFTER_LABEL: Record<ClearAfter, PlainKey> = {
	"30m": "people.status.clearAfter.30m",
	"1h": "people.status.clearAfter.1h",
	"4h": "people.status.clearAfter.4h",
	today: "people.status.clearAfter.today",
	week: "people.status.clearAfter.week",
	never: "people.status.clearAfter.never",
}

/** What one end is called in the picker. */
export function clearAfterLabel(choice: ClearAfter): string {
	return t(CLEAR_AFTER_LABEL[choice])
}

const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000

/**
 * The moment a status chosen now clears itself, as epoch-ms; 0 is the server's "never".
 *
 * The two calendar answers are resolved in the **account's** zone and not the browser's
 * (`core/i18n/format.ts`): "today" is the end of the day the person is living in, and "this week" is
 * the end of Sunday, en-GB's last day of the week. The client computes the moment rather than
 * sending a duration because the person chose a wall-clock edge, and only the client knows which
 * wall they are reading.
 */
export function untilFor(choice: ClearAfter, now: number): number {
	switch (choice) {
		case "30m":
			return now + 30 * MINUTE_MS
		case "1h":
			return now + HOUR_MS
		case "4h":
			return now + 4 * HOUR_MS
		case "today":
			return endOfDay(now)
		case "week":
			return endOfWeek(now)
		case "never":
			return 0
	}
}

// ---- the presets ----

/**
 * A status somebody can set in one press. The words on the button are the words it writes, so the
 * preset is one key and not two, and `away` is present only on the one preset that is a claim about
 * being reachable at all rather than a line about the day.
 */
export interface StatusPreset {
	id: string
	emoji: string
	label: PlainKey
	clearAfter: ClearAfter
	away?: boolean
}

/** The offered presets, in the order they are drawn. Every emoji is a member of the palette. */
export const STATUS_PRESETS = [
	{ id: "meeting", emoji: "🗓️", label: "people.status.preset.meeting", clearAfter: "1h" },
	{ id: "lunch", emoji: "🍽️", label: "people.status.preset.lunch", clearAfter: "1h" },
	{ id: "sick", emoji: "🤒", label: "people.status.preset.sick", clearAfter: "today" },
	{ id: "holiday", emoji: "🌴", label: "people.status.preset.holiday", clearAfter: "never" },
	{ id: "commuting", emoji: "🚌", label: "people.status.preset.commuting", clearAfter: "30m" },
	{ id: "focus", emoji: "🎧", label: "people.status.preset.focus", clearAfter: "4h" },
	{ id: "away", emoji: "💤", label: "people.status.preset.away", clearAfter: "never", away: true },
] as const satisfies readonly StatusPreset[]

// ---- how a status is written ----

/** The status as its form holds it while it is being edited. */
export interface StatusDraft {
	emoji: string
	text: string
	/** `keep` is the end the stored status already carries, left as it is. */
	clearAfter: ClearAfter | "keep"
	away: boolean
}

/** The form's starting point: what is stored, with the end it already has kept rather than reset. */
export function draftOf(fields: StatusFields): StatusDraft {
	return {
		emoji: fields.emoji,
		text: fields.text,
		clearAfter: fields.until === 0 ? "never" : "keep",
		away: fields.away,
	}
}

/**
 * Which end the picker is standing on, which is the draft's own answer except where that answer has
 * stopped existing.
 *
 * `keep` is an answer only while there is an end to keep, and the stored status moves under an open
 * dialog: the expiry job clears it within the minute it named, and a second tab can clear it at any
 * moment. The option would then be gone from the list while the draft still named it, and a
 * `<select>` whose value matches no option of its own shows a choice nobody made — while the save
 * would write the end that vanished, which is none at all. So the two are made to agree here, on the
 * offered end that says the same thing: `never`.
 */
export function clearAfterShown(
	fields: StatusFields,
	draft: StatusDraft,
): StatusDraft["clearAfter"] {
	return draft.clearAfter === "keep" && fields.until === 0 ? "never" : draft.clearAfter
}

/**
 * A preset pressed into the form. It fills in the whole line, and the away claim is only ever
 * *raised* by one: a preset that says nothing about being away leaves a claim the person made
 * themselves alone, because "Lunch" is not a statement that somebody is back.
 */
export function withPreset(draft: StatusDraft, preset: StatusPreset): StatusDraft {
	return {
		emoji: preset.emoji,
		text: t(preset.label),
		clearAfter: preset.clearAfter,
		away: preset.away ?? draft.away,
	}
}

/**
 * The write one save makes: the fields that actually changed and no others, so fixing a typo does
 * not restate the end and ticking *away* alone leaves the line untouched (`protocol.md` §3.2).
 *
 * An end without an emoji and without a line is dropped on the way out. The server accepts it, and
 * the job would clear it a minute later for no visible reason; clearing a status is the three fields
 * together, which is exactly what a form emptied by hand produces here.
 */
export function statusPatch(current: StatusFields, draft: StatusDraft, now: number): ProfilePatch {
	const emoji = draft.emoji
	const text = draft.text.trim()
	const kept = draft.clearAfter === "keep" ? current.until : untilFor(draft.clearAfter, now)
	const until = emoji === "" && text === "" ? 0 : kept

	const patch: ProfilePatch = {}
	if (emoji !== current.emoji) patch.statusEmoji = emoji
	if (text !== current.text) patch.statusText = text
	if (until !== current.until) patch.statusUntil = until
	if (draft.away !== current.away) patch.statusAway = draft.away
	return patch
}

/**
 * Clearing a status. The three fields go together because that is what the route calls "cleared",
 * and the away claim is deliberately not among them: it has no end, and somebody who takes their
 * lunch line down has not said they are back at their desk.
 */
export function clearedStatus(): ProfilePatch {
	return { statusEmoji: "", statusText: "", statusUntil: 0 }
}
